import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  ShoppingCart,
  DollarSign,
  FileText,
  Utensils,
  AlertTriangle,
  Banknote,
} from "lucide-react";

// Dashboard service
import dashboardService from "../service/dashboard";

// Dashboard components
import StatCard from "../components/dashboard/StatCard";
import MealPerformanceCard from "../components/dashboard/MealPerformanceCard";
import TodayAvailabilityCard from "../components/dashboard/TodayAvailabilityCard";
import SubscriptionMetricsCard from "../components/dashboard/SubscriptionMetricsCard";
import OperationalAlertsCard from "../components/dashboard/OperationalAlertsCard";
import RecentActivityCard from "../components/dashboard/RecentActivityCard";
import RestaurantOverviewCard from "../components/dashboard/RestaurantOverviewCard";
import DashboardControls from "../components/dashboard/DashboardControls";

export default function Home() {
  const { t } = useTranslation();

  // Fetch all dashboard data using React Query
  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: () => dashboardService.getAllDashboardData(),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Data considered fresh for 2 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onSuccess: (data) => {
      // Debug logging - remove in production
      console.log("Dashboard data received:", data);
    },
    onError: (error) => {
      console.error("Dashboard data fetch error:", error);
    },
  });

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num?.toString() || "0";
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "JOD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate change percentage
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      value: `${Math.abs(change).toFixed(1)}%`,
      type: change >= 0 ? "positive" : "negative",
    };
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("dashboard.error")}
          </h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("dashboard.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <DashboardControls
          onRefresh={refetch}
          isLoading={isLoading}
          lastUpdated={dashboardData ? new Date() : null}
        />
      </div>

      <div className="p-6">
        {/* Restaurant Overview */}
        <RestaurantOverviewCard
          restaurantData={dashboardData?.overview}
          isLoading={isLoading}
        />

        {/* Overview Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("dashboard.todaysStats")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title={t("dashboard.ordersToday")}
              value={formatNumber(dashboardData?.overview?.ordersToday)}
              change={
                calculateChange(
                  dashboardData?.overview?.ordersToday,
                  dashboardData?.overview?.ordersYesterday
                )?.value
              }
              changeType={
                calculateChange(
                  dashboardData?.overview?.ordersToday,
                  dashboardData?.overview?.ordersYesterday
                )?.type
              }
              icon={<ShoppingCart className="w-6 h-6" />}
              isLoading={isLoading}
            />
            <StatCard
              title={t("dashboard.revenueToday")}
              value={formatCurrency(dashboardData?.overview?.todayRevenue)}
              change={
                calculateChange(
                  dashboardData?.overview?.revenueToday,
                  dashboardData?.overview?.revenueYesterday
                )?.value
              }
              changeType={
                calculateChange(
                  dashboardData?.overview?.revenueToday,
                  dashboardData?.overview?.revenueYesterday
                )?.type
              }
              icon={<Banknote className="w-6 h-6" />}
              isLoading={isLoading}
            />
            <StatCard
              title={t("dashboard.activeSubscriptions")}
              value={formatNumber(dashboardData?.overview?.activeSubscriptions)}
              change={
                calculateChange(
                  dashboardData?.overview?.activeSubscriptions,
                  dashboardData?.overview?.subscriptionsLastWeek
                )?.value
              }
              changeType={
                calculateChange(
                  dashboardData?.overview?.activeSubscriptions,
                  dashboardData?.overview?.subscriptionsLastWeek
                )?.type
              }
              icon={<FileText className="w-6 h-6" />}
              isLoading={isLoading}
            />
            <StatCard
              title={t("dashboard.availableMeals")}
              value={formatNumber(dashboardData?.overview?.availableMealsToday)}
              icon={<Utensils className="w-6 h-6" />}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          {/* Left Column - 2/3 width */}
          <div className="xl:col-span-2 space-y-6">
            {/* Revenue Stats */}
            {/* <RevenueStatsCard
              revenueData={dashboardData?.revenueStats}
              isLoading={isLoading}
              chartType={revenueChartType}
              onChartTypeChange={setRevenueChartType}
            /> */}

            {/* Meal Performance */}
            <MealPerformanceCard
              meals={dashboardData?.topMeals}
              isLoading={isLoading}
            />

            {/* Subscription Metrics */}
            <SubscriptionMetricsCard
              metrics={dashboardData?.subscriptionMetrics}
              isLoading={isLoading}
            />
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* Today's Availability */}
            <TodayAvailabilityCard
              availability={dashboardData?.todayAvailability}
              isLoading={isLoading}
            />

            {/* Operational Alerts */}
            <OperationalAlertsCard
              alerts={dashboardData?.operationalAlerts}
              isLoading={isLoading}
            />

            {/* Recent Activity */}
            <RecentActivityCard
              activities={dashboardData?.recentActivity}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-10 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg p-6 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-900 font-medium">
                {t("dashboard.loading")}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
